import FilePdfOutlined from "@ant-design/icons/lib/icons/FilePdfOutlined";
import theme from "@shared/utils/themeColors";
import { formatDistanceToNow } from "date-fns";
import {
  getTestStatusBadgeClass,
  getTestStatusText,
} from "../../../shared/utils/test.utils";
import { ProcessedTest } from "../types/pdf-processing.types";

interface ProcessedTestCardProps {
  test: ProcessedTest;
  onView: () => void;
  onViewPdf: () => void;
}

export const ProcessedTestCard = ({
  test,
  onView,
  onViewPdf,
}: ProcessedTestCardProps) => {
  const formatDate = (date: Date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (error) {
      return "Unknown date";
    }
  };

  return (
    <div
      className={`${theme.bg.primary} shadow-sm rounded-sm border border-gray-200 dark:border-gray-700 p-4`}
    >
      <div className="sm:flex sm:justify-between sm:items-start">
        {/* Left side */}
        <div className="grow flex items-start">
          {/* PDF icon */}
          <div className="flex-shrink-0 mr-4">
            <FilePdfOutlined style={{ fontSize: "26px" }} />
          </div>

          {/* Test info */}
          <div className="grow">
            <div className="flex items-center mb-1">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mr-2">
                {test.name}
              </h2>
              <div
                className={`text-xs inline-flex font-medium rounded-full text-center px-2.5 py-1 ${getTestStatusBadgeClass(
                  test.status
                )}`}
              >
                {getTestStatusText(test.status)}
              </div>
            </div>
            <div className={`text-sm ${theme.text.muted}`}>
              Total Questions: {test.questionCount}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>
              <span className="mr-3">
                Created: {formatDate(test.createdAt.toDate())}
              </span>
              {test.updatedAt && (
                <span>Updated: {formatDate(test.updatedAt.toDate())}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex flex-col sm:flex-row items-end sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 mt-2 sm:mt-0">
          {test.pdfUrl && (
            <button
              onClick={onViewPdf}
              className="btn-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-indigo-500 shadow-sm"
            >
              <svg
                className="w-4 h-4 fill-current text-indigo-500 mr-1.5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>View PDF</span>
            </button>
          )}
          <button
            onClick={onView}
            className="btn-sm bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-indigo-500 shadow-sm"
          >
            <svg
              className="w-4 h-4 fill-current text-indigo-500 mr-1.5"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            >
              <path d="M13.95.879a3 3 0 0 0-4.243 0L1.293 9.293a1 1 0 0 0-.274.51l-1 5a1 1 0 0 0 1.177 1.177l5-1a1 1 0 0 0 .511-.273l8.414-8.414a3 3 0 0 0 0-4.242L13.95.879ZM11.12 2.293a1 1 0 0 1 1.414 0l1.172 1.172a1 1 0 0 1 0 1.414l-8.2 8.2-3.232.646.646-3.232 8.2-8.2Z"></path>
              <path d="M10 14a1 1 0 1 0 0 2h5a1 1 0 1 0 0-2h-5Z"></path>
            </svg>
            <span>Edit Test</span>
          </button>
        </div>
      </div>
    </div>
  );
};
